import { forwardRef, HTMLAttributes, useRef, useState } from 'react'
import { useIsomorphicLayoutEffect, useKey } from 'react-use'
import gsap from 'gsap'
import Lenis from 'lenis'

import { ModalActions } from '@/components/base/Modal'
import Spinner from '@/components/base/Spinner'
import SVGIcon from '@/components/base/SVGIcon'
import PageBuilder from '@/components/layout/Builder'
import { mapPageStaticProps } from '@/utils/pageData'
import { getPromiseFromFetch } from '@/utils/request'
import { API } from '@/constants'

import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

export type ArticleProps = HTMLAttributes<HTMLDivElement> &
  ModalActions & { slug?: string }

const Article = forwardRef<HTMLDivElement, ArticleProps>(
  ({ slug, onClose, className, ...props }, ref) => {
    const [pageData, setPageData] = useState<WPPageData>(null!)
    // eslint-disable-next-line no-unused-vars
    const [lenis, setLenis] = useState<Lenis>()
    const wrapper = useRef<HTMLDivElement>(null!)
    const content = useRef<HTMLDivElement>(null!)

    useIsomorphicLayoutEffect(() => {
      const fetch = async () => {
        const { data } = await getPromiseFromFetch({
          method: 'get',
          url: `${API.BASE}/posts?slug=${slug}`
        })
        const pageData = await mapPageStaticProps(data[0])
        setPageData(pageData)
      }
      fetch()
    }, [slug])

    useIsomorphicLayoutEffect(() => {
      const lenis = new Lenis({
        smoothWheel: true,
        wrapper: wrapper.current,
        content: content.current
      })

      const tick = (time: number) => {
        lenis.raf(time * 1000)
      }

      gsap.ticker.add(tick)

      setLenis(lenis)

      return () => {
        gsap.ticker.remove(tick)
        lenis.destroy()
        setLenis(undefined)
      }
    }, [])

    useKey('Escape', onClose)

    return (
      <div
        ref={ref}
        className={cn([
          'fixed left-0 top-0 z-modal h-full w-full bg-dark-blue/30',
          'flex p-0 md:p-gutter',
          className
        ])}
        {...props}
      >
        <div className="relative m-auto h-full w-full max-w-[64.5rem]">
          <button
            className={cn([
              'absolute right-gutter top-gutter z-10',
              'flex h-[4.4rem] w-[4.4rem] items-center justify-center rounded-full bg-dark-blue'
            ])}
            onClick={onClose}
          >
            <span className="sr-only">Chiudi</span>
            <SVGIcon
              symbol="close-icon"
              className="h-[1.4rem] w-[1.4rem] fill-white"
            />
          </button>

          <div
            ref={wrapper}
            className="h-full min-h-full w-full overflow-auto bg-white pt-[6rem] scrollbar-hide md:rounded-[3rem]"
          >
            <div
              ref={content}
              className={cn(['h-auto w-full', 'modal group text-dark-blue'])}
            >
              {pageData ? (
                <PageBuilder pageData={pageData} />
              ) : (
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  <Spinner
                    variant="medium"
                    className="[&>svg]:stroke-dark-blue"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* <div
          className={cn([
            'group absolute bottom-0 right-0 top-0 z-scrollbar hidden w-[1.2rem]',
            {
              visible: needsScrollbar,
              invisible: !needsScrollbar
            }
          ])}
        >
          <div ref={track} className="relative h-full w-full">
            <div
              ref={thumb}
              className={cn([
                'absolute left-0 top-0 w-full',
                'before:absolute before:bottom-[.2rem] before:left-[.4rem] before:top-[.2rem] before:w-[.6rem] before:rounded-full before:bg-dark-blue'
              ])}
            />
          </div>
        </div> */}
      </div>
    )
  }
)

export default Article
